/**
 * @component OrderCapacitySettings
 *
 * The SIB's capacity per service with an effective date (SIB-941).
 *
 * Every service shows the capacity in effect today ("Aktuell") and, when one is scheduled, the
 * planned capacity with its date ("Gültig ab"). A change to an existing capacity needs its own
 * date per service, is confirmed in plain text before it is saved, and only the services that
 * actually changed are sent. A planned change can be edited or dropped while its date is still in
 * the future; once the date has arrived it is history and only a new change can follow.
 *
 * Once Union opens the next year (SIB-928), a tab per open year appears. The next year starts out
 * with the current values carried over and marked as such; they are a display default only.
 * Opening the tab saves nothing - only a value the SIB actually changes is sent, dated to the
 * year's `nextChangeValidFrom` (1 January). An unchanged value simply continues into the new year.
 */

import {
  Alert,
  Badge,
  Box,
  Button,
  Collapsible,
  Field,
  HStack,
  IconButton,
  Skeleton,
  SkeletonText,
  Stack,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react'
import { LuMinus, LuPlus } from 'react-icons/lu'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from '@/components/ui/toaster/toasterHelpers'
import { DismissChangesDialog } from '@/components/modals/DismissChangesDialog'
import {
  dropPlannedCapacityChange,
  getCapacityChangeHistory,
  getOrderCapacities,
  getOrderCapacityYears,
  updateOrderCapacities,
} from '@/api/settings.api'
import type {
  CapacityChangeDto,
  OrderCapacityYearsResponse,
  UpdateOrderSettingsDto,
} from '@/api/dto/settings.dto'
import type { CapacitySource, CapacityYear, OrderSettings } from '@/common/settings.common'
import { useUnsavedChangesGuardCustom } from '@/hooks/useUnsavedChangesGuard'
import { useUserStore } from '@/stores/user.store'
import {
  buildHistoryRows,
  backendMessageOf,
  formatIsoDateDE,
  isAlreadyInEffectError,
  parseCapacityInput,
  todayInBusinessZone,
  validateEffectiveDate,
  type HistoryRow,
  type IsoDate,
  type PendingValueChange,
  type PlannedValue,
} from '@/helper/valueChange.helper'
import { SettingsActionBar } from './SettingsActionBar'
import { SettingsInput } from './SettingsControls'
import { FormLayout } from '@/components/layout/FormLayout'
import { EffectiveDatePicker } from '@/components/settings/EffectiveDatePicker'
import { ServiceValueSummary } from '@/components/settings/ServiceValueSummary'
import { ValueChangeConfirmDialog } from '@/components/settings/ValueChangeConfirmDialog'
import { DropPlannedChangeDialog } from '@/components/settings/DropPlannedChangeDialog'
import { ValueChangeHistoryTable } from '@/components/settings/ValueChangeHistoryTable'

type ServiceCode = 'TOJ' | 'CONSULTING' | 'TRAINING' | 'PILOT'

const SERVICES: {
  code: ServiceCode
  label: string
  required: boolean
  helper: string
}[] = [
  {
    code: 'TOJ',
    label: 'TOJ',
    required: true,
    helper: 'Training on the Job mit oder ohne Kunde in Einheiten (1 Einheit = 2,5 h)',
  },
  {
    code: 'CONSULTING',
    label: 'Beratung',
    required: true,
    helper: 'Beratung in Stunden, es sind halbe Einheiten möglich (1 Einheit = 1 h)',
  },
  {
    code: 'TRAINING',
    label: 'Schulung',
    required: true,
    helper: 'Schulung in Tagen, es sind halbe Einheiten möglich (1 Einheit = 8 h)',
  },
  {
    code: 'PILOT',
    label: 'Pilot',
    required: false,
    helper: 'Pilot-Einheiten (1 Einheit = 1 h, nur ganze Zahlen)',
  },
]

const SERVICE_LABEL: Record<string, string> = Object.fromEntries(
  SERVICES.map(s => [s.code, s.label])
)

/** What the backend knows about one service. */
type ServiceRecord = {
  setting: OrderSettings | null
  activeValue: number | null
  activeSince: string | null
  planned: PlannedValue[]
  /** Only set for a planning year other than the running one (SIB-928). */
  source: CapacitySource | null
}

/** What the user typed for one service. */
type ServiceDraft = {
  input: string
  validFrom: IsoDate | ''
  editing: PlannedValue | null
}

type Records = Record<ServiceCode, ServiceRecord>
type Drafts = Record<ServiceCode, ServiceDraft>

const EMPTY_RECORD: ServiceRecord = {
  setting: null,
  activeValue: null,
  activeSince: null,
  planned: [],
  source: null,
}

function toPlannedValues(
  planned: {
    id: number
    capacity: number
    validFrom: string
    createdBy: string
    createdAt: string
  }[]
): PlannedValue[] {
  return planned.map(p => ({
    id: p.id,
    value: p.capacity,
    validFrom: p.validFrom,
    createdBy: p.createdBy,
    createdAt: p.createdAt,
  }))
}

function toRecords(settings: OrderSettings[]): Records {
  const out = {} as Records
  for (const s of SERVICES) {
    const setting = settings.find(x => x.service?.code === s.code) ?? null
    out[s.code] = setting
      ? {
          setting,
          activeValue: setting.capacity,
          activeSince: setting.validFrom ?? null,
          planned: toPlannedValues(setting.plannedChanges ?? []),
          source: null,
        }
      : EMPTY_RECORD
  }
  return out
}

/** A planning year that hasn't started yet, as the SIB sees it (SIB-928). */
function toYearRecords(year: CapacityYear): Records {
  const out = {} as Records
  for (const s of SERVICES) {
    const entry = year.services.find(x => x.service?.code === s.code)
    out[s.code] = entry
      ? {
          setting: null,
          activeValue: entry.capacity,
          activeSince: entry.validFrom,
          planned: toPlannedValues(entry.plannedChanges ?? []),
          source: entry.source,
        }
      : { ...EMPTY_RECORD, source: 'NONE' }
  }
  return out
}

/** How saving works in the year on screen (SIB-928). */
type YearMode = {
  /** null while the planning years are unknown; the screen then works as before SIB-928. */
  year: number | null
  current: boolean
  editable: boolean
  /**
   * The date a new value takes effect in a year that hasn't started (its `nextChangeValidFrom`).
   * null in the running year, where the SIB picks the month.
   */
  fixedValidFrom: IsoDate | null
}

const LEGACY_MODE: YearMode = { year: null, current: true, editable: true, fixedValidFrom: null }

function yearMode(year: CapacityYear): YearMode {
  return {
    year: year.year,
    current: year.current,
    editable: year.editable,
    fixedValidFrom: year.current ? null : year.nextChangeValidFrom,
  }
}

function baselineInput(record: ServiceRecord): string {
  return record.activeValue === null ? '' : String(record.activeValue)
}

function toDrafts(records: Records): Drafts {
  const out = {} as Drafts
  for (const s of SERVICES) {
    out[s.code] = { input: baselineInput(records[s.code]), validFrom: '', editing: null }
  }
  return out
}

/** A change to an existing capacity must name its date; the very first value takes effect now. */
function needsEffectiveDate(record: ServiceRecord): boolean {
  return !!record.setting?.validFrom
}

/**
 * Whether the SIB has to pick a date for this change. In a year that hasn't started the date is
 * given; only editing one of its planned changes asks for a date again.
 */
function needsPickedDate(record: ServiceRecord, draft: ServiceDraft, mode: YearMode): boolean {
  if (draft.editing) return true
  if (mode.fixedValidFrom) return false
  return needsEffectiveDate(record)
}

/** The date a pending change is sent with; null only for a first value in the running year. */
function effectiveDateOf(record: ServiceRecord, draft: ServiceDraft, mode: YearMode) {
  if (needsPickedDate(record, draft, mode)) return draft.validFrom || null
  return mode.fixedValidFrom
}

function isDirty(record: ServiceRecord, draft: ServiceDraft): boolean {
  if (draft.editing) {
    return (
      draft.input.trim() !== String(draft.editing.value) ||
      draft.validFrom !== draft.editing.validFrom
    )
  }
  return draft.input.trim() !== baselineInput(record)
}

function toHistoryRows(changes: CapacityChangeDto[]): HistoryRow[] {
  return buildHistoryRows(
    changes.map(c => ({
      id: c.id,
      serviceCode: c.service?.code ?? '',
      serviceLabel:
        SERVICE_LABEL[c.service?.code ?? ''] ?? c.service?.name ?? c.service?.code ?? '',
      value: c.capacity,
      validFrom: c.validFrom,
      state: c.state,
      createdBy: c.createdBy,
      createdAt: c.createdAt,
      discardedBy: c.discardedBy,
      discardedAt: c.discardedAt,
    }))
  )
}

/** The planning years a SIB can switch between, earliest first (SIB-928). */
function openYearsOf(res: OrderCapacityYearsResponse | null): CapacityYear[] {
  return (res?.years ?? []).filter(y => y.open).sort((a, b) => a.year - b.year)
}

/** The year on screen: the one asked for while it is open, otherwise the running year. */
function visibleYearOf(
  res: OrderCapacityYearsResponse | null,
  wanted: number | null
): CapacityYear | null {
  const open = openYearsOf(res)
  return open.find(y => y.year === wanted) ?? open.find(y => y.current) ?? null
}

/** The running year keeps using GET /settings/orders; another year uses its own values. */
function recordsFor(year: CapacityYear | null, current: Records): Records {
  return !year || year.current ? current : toYearRecords(year)
}

async function loadCapacityYears(): Promise<OrderCapacityYearsResponse | null> {
  try {
    return await getOrderCapacityYears()
  } catch {
    // Without the years the screen still works for the running year, exactly as before SIB-928.
    return null
  }
}

export function OrderCapacitySettings({ today }: { today?: IsoDate } = {}) {
  const todayIso = today ?? todayInBusinessZone()
  const user = useUserStore(s => s.user)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [cancelOpen, setCancelOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [showErrors, setShowErrors] = useState(false)

  const [currentRecords, setCurrentRecords] = useState<Records>(() => toRecords([]))
  const [drafts, setDrafts] = useState<Drafts>(() => toDrafts(toRecords([])))

  const [capacityYears, setCapacityYears] = useState<OrderCapacityYearsResponse | null>(null)
  const [selectedYear, setSelectedYear] = useState<number | null>(null)
  // load() is stable, so it reads the chosen year from here to keep that tab after a reload.
  const selectedYearRef = useRef<number | null>(null)

  const openYears = useMemo(() => openYearsOf(capacityYears), [capacityYears])
  const visibleYear = useMemo(
    () => visibleYearOf(capacityYears, selectedYear),
    [capacityYears, selectedYear]
  )
  const mode = useMemo(() => (visibleYear ? yearMode(visibleYear) : LEGACY_MODE), [visibleYear])
  const records = useMemo(
    () => recordsFor(visibleYear, currentRecords),
    [visibleYear, currentRecords]
  )

  const [history, setHistory] = useState<HistoryRow[]>([])
  const [historyAvailable, setHistoryAvailable] = useState(true)
  const [historyOpen, setHistoryOpen] = useState(false)

  const [dropTarget, setDropTarget] = useState<{
    code: ServiceCode
    planned: PlannedValue
  } | null>(null)
  const [dropping, setDropping] = useState(false)

  /* ----------------------------- LOAD DATA ----------------------------- */

  const load = useCallback(async () => {
    const [res, years] = await Promise.all([getOrderCapacities(), loadCapacityYears()])
    const current = toRecords(res.settings ?? [])
    setCurrentRecords(current)
    setCapacityYears(years)
    setDrafts(toDrafts(recordsFor(visibleYearOf(years, selectedYearRef.current), current)))

    try {
      const h = await getCapacityChangeHistory()
      setHistory(toHistoryRows(h.changes ?? []))
      setHistoryAvailable(true)
    } catch {
      setHistory([])
      setHistoryAvailable(false)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    async function run() {
      try {
        setLoading(true)
        await load()
      } catch {
        if (!cancelled) {
          toast.error(
            'Kapazitäten konnten nicht geladen werden. Bitte versuchen Sie es später erneut.'
          )
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [load])

  /* ---------------------------- VALIDATION ---------------------------- */

  const dirtyCodes = useMemo(
    () => SERVICES.map(s => s.code).filter(code => isDirty(records[code], drafts[code])),
    [records, drafts]
  )
  const dirty = dirtyCodes.length > 0

  const fieldProblems = useMemo(() => {
    const out = {} as Record<ServiceCode, { value: boolean; date: boolean }>
    for (const s of SERVICES) {
      const record = records[s.code]
      const draft = drafts[s.code]
      const changed = isDirty(record, draft)
      const valueProblem = changed && parseCapacityInput(draft.input) === null
      const dateProblem =
        changed &&
        needsPickedDate(record, draft, mode) &&
        validateEffectiveDate(draft.validFrom, todayIso) !== null
      out[s.code] = { value: valueProblem, date: dateProblem }
    }
    return out
  }, [records, drafts, todayIso, mode])

  /** The values that will be in effect once everything dirty is saved (unchanged ones stay). */
  const resultingValues = useMemo(
    () =>
      SERVICES.map(s => {
        const record = records[s.code]
        const draft = drafts[s.code]
        if (isDirty(record, draft)) return parseCapacityInput(draft.input) ?? 0
        return record.activeValue ?? 0
      }),
    [records, drafts]
  )
  const hasAtLeastOnePositive = resultingValues.some(n => n > 0)

  const allFieldsValid = SERVICES.every(
    s => !fieldProblems[s.code].value && !fieldProblems[s.code].date
  )
  const allValid = allFieldsValid && hasAtLeastOnePositive

  const pendingChanges: PendingValueChange[] = useMemo(
    () =>
      dirtyCodes.map(code => {
        const record = records[code]
        const draft = drafts[code]
        return {
          serviceCode: code,
          serviceLabel: SERVICE_LABEL[code],
          activeValue: record.activeValue,
          newValue: parseCapacityInput(draft.input) ?? 0,
          validFrom: effectiveDateOf(record, draft, mode),
          replaces: draft.editing,
        }
      }),
    [dirtyCodes, records, drafts, mode]
  )

  /* ------------------------------ ACTIONS ------------------------------ */

  const { isOpen, confirmNavigation, cancelNavigation } = useUnsavedChangesGuardCustom(dirty)

  function updateDraft(code: ServiceCode, patch: Partial<ServiceDraft>) {
    setDrafts(prev => ({ ...prev, [code]: { ...prev[code], ...patch } }))
  }

  function resetToOriginal() {
    setDrafts(toDrafts(records))
    setShowErrors(false)
  }

  /** Switching years only changes what is shown; it never writes anything (SIB-928). */
  function selectYear(year: number) {
    if (dirty) return
    selectedYearRef.current = year
    setSelectedYear(year)
    setDrafts(toDrafts(recordsFor(visibleYearOf(capacityYears, year), currentRecords)))
    setShowErrors(false)
  }

  function startEditing(code: ServiceCode, planned: PlannedValue) {
    updateDraft(code, {
      input: String(planned.value),
      validFrom: planned.validFrom,
      editing: planned,
    })
  }

  function stopEditing(code: ServiceCode) {
    updateDraft(code, { input: baselineInput(records[code]), validFrom: '', editing: null })
  }

  function handleSaveClick() {
    setShowErrors(true)
    if (!allValid || !dirty) return
    setConfirmOpen(true)
  }

  async function reloadAfterTakingEffect() {
    toast.info(
      'Die Änderung ist inzwischen wirksam',
      'Das Datum dieser Änderung ist erreicht; sie kann nicht mehr bearbeitet oder verworfen werden. Die Ansicht wurde aktualisiert.'
    )
    try {
      await load()
    } catch {
      /* the toast already explains the state; the next reload will settle it */
    }
  }

  async function doSave() {
    if (saving || pendingChanges.length === 0) return
    setSaving(true)

    const payload: UpdateOrderSettingsDto = {
      settings: pendingChanges.map(c => ({
        serviceCode: c.serviceCode,
        capacity: c.newValue,
        ...(c.validFrom ? { validFrom: c.validFrom } : {}),
      })),
    }

    try {
      await updateOrderCapacities(payload)

      // Editing a planned change onto a different date: the backend replaces a plan only for the
      // same date, so the old plan is dropped explicitly. Dropping after writing means a failure
      // here leaves two visible plans rather than none.
      const leftovers: string[] = []
      for (const c of pendingChanges) {
        if (c.replaces && c.replaces.validFrom !== c.validFrom) {
          try {
            await dropPlannedCapacityChange(c.replaces.id)
          } catch {
            leftovers.push(`${c.serviceLabel} ab ${formatIsoDateDE(c.replaces.validFrom)}`)
          }
        }
      }

      setConfirmOpen(false)
      const effective = pendingChanges
        .map(c =>
          c.validFrom
            ? `${c.serviceLabel} gilt ab ${formatIsoDateDE(c.validFrom)}`
            : `${c.serviceLabel} gilt ab sofort`
        )
        .join(', ')
      toast.success('Änderung gespeichert', `${effective}.`)
      if (leftovers.length > 0) {
        toast.warning(
          'Bisherige Planung noch vorhanden',
          `Die bisher geplante Änderung (${leftovers.join(', ')}) konnte nicht verworfen werden. Bitte verwerfen Sie sie manuell.`
        )
      }
      await load()
      setShowErrors(false)
    } catch (err) {
      setConfirmOpen(false)
      if (isAlreadyInEffectError(err)) {
        await reloadAfterTakingEffect()
      } else {
        toast.error(
          'Kapazitäten konnten nicht gespeichert werden',
          backendMessageOf(err) ?? 'Bitte versuchen Sie es erneut.'
        )
      }
    } finally {
      setSaving(false)
    }
  }

  async function doDrop() {
    if (!dropTarget || dropping) return
    setDropping(true)
    try {
      await dropPlannedCapacityChange(dropTarget.planned.id)
      toast.success(
        'Geplante Änderung verworfen',
        `${SERVICE_LABEL[dropTarget.code]}: Der aktuelle Wert gilt weiterhin.`
      )
      setDropTarget(null)
      await load()
    } catch (err) {
      setDropTarget(null)
      if (isAlreadyInEffectError(err)) {
        await reloadAfterTakingEffect()
      } else {
        toast.error(
          'Geplante Änderung konnte nicht verworfen werden',
          backendMessageOf(err) ?? 'Bitte versuchen Sie es erneut.'
        )
      }
    } finally {
      setDropping(false)
    }
  }

  /* ------------------------------ RENDER ------------------------------- */

  return (
    <Box data-testid="order-capacity-settings" position="relative">
      {/* guard dialog (route change) */}
      <DismissChangesDialog
        isOpen={isOpen}
        onClose={cancelNavigation}
        onDismiss={() => {
          resetToOriginal()
          confirmNavigation()
        }}
        onSave={() => {
          // Saving always goes through the confirmation; the page stays until it is done.
          handleSaveClick()
        }}
        onNavigate={cancelNavigation}
      />

      <FormLayout.Root>
        <FormLayout.Section>
          <FormLayout.Title>Auftragsvolumen eintragen</FormLayout.Title>
          <FormLayout.Description>
            Bitte tragen Sie hier Ihr Auftragsvolumen je Leistung ein. Eine Änderung gilt ab dem
            gewählten Datum; bis dahin bleibt der aktuelle Wert bestehen.
          </FormLayout.Description>
        </FormLayout.Section>

        {!loading && visibleYear && openYears.length > 1 && (
          <Stack gap={2}>
            <Tabs.Root
              value={String(visibleYear.year)}
              onValueChange={e => selectYear(Number(e.value))}
              variant="subtle"
            >
              <Tabs.List aria-label="Planungsjahr">
                {openYears.map(y => (
                  <Tabs.Trigger
                    key={y.year}
                    value={String(y.year)}
                    disabled={dirty && y.year !== visibleYear.year}
                  >
                    {y.year}
                  </Tabs.Trigger>
                ))}
              </Tabs.List>
            </Tabs.Root>
            {dirty && (
              <Text fontSize="sm" color="fg.muted">
                Bitte speichern oder verwerfen Sie Ihre Änderungen, bevor Sie das Jahr wechseln.
              </Text>
            )}
          </Stack>
        )}

        {!loading && visibleYear && (
          <YearNotice year={visibleYear} openYears={openYears} records={records} />
        )}

        {loading ? (
          <Stack direction={{ base: 'column', md: 'row' }} w="full" align="flex-start" gap={6}>
            {SERVICES.map(s => (
              <VStack key={s.code} flex={1} w="full" align="flex-start">
                <SkeletonText noOfLines={1} w="60px" />
                <SkeletonText noOfLines={2} gap={1} />
                <Skeleton h="44px" w="full" />
                <Skeleton h="44px" w="full" />
              </VStack>
            ))}
          </Stack>
        ) : (
          <Box>
            <Stack direction={{ base: 'column', md: 'row' }} align="flex-start" gap={6}>
              {SERVICES.map(s => {
                const record = records[s.code]
                const draft = drafts[s.code]
                const problems = fieldProblems[s.code]
                const changed = isDirty(record, draft)
                return (
                  <CapacityField
                    key={s.code}
                    code={s.code}
                    label={s.label}
                    required={s.required}
                    helper={s.helper}
                    record={record}
                    draft={draft}
                    changed={changed}
                    valueError={showErrors && problems.value}
                    dateRequired={changed && needsPickedDate(record, draft, mode)}
                    showDateError={showErrors}
                    today={todayIso}
                    disabled={saving}
                    readOnly={!mode.editable}
                    planningYear={mode.current ? null : mode.year}
                    fixedValidFrom={mode.fixedValidFrom}
                    onInput={v => updateDraft(s.code, { input: v })}
                    onDate={v => updateDraft(s.code, { validFrom: v })}
                    onEditPlanned={p => startEditing(s.code, p)}
                    onStopEditing={() => stopEditing(s.code)}
                    onDropPlanned={p => setDropTarget({ code: s.code, planned: p })}
                  />
                )
              })}
            </Stack>

            {!hasAtLeastOnePositive && (
              <Alert.Root status="error">
                <Alert.Indicator />
                <Alert.Description>
                  Mindestens eine Kapazität muss größer als 0 sein.
                </Alert.Description>
              </Alert.Root>
            )}
          </Box>
        )}

        {!loading && (
          <Collapsible.Root open={historyOpen} onOpenChange={e => setHistoryOpen(e.open)}>
            <FormLayout.Section>
              <HStack gap={2}>
                <FormLayout.Title>Verlauf</FormLayout.Title>
                <Collapsible.Trigger asChild>
                  <IconButton
                    variant="ghost"
                    aria-label={historyOpen ? 'Verlauf ausblenden' : 'Verlauf anzeigen'}
                    aria-expanded={historyOpen}
                  >
                    {historyOpen ? <LuMinus /> : <LuPlus />}
                  </IconButton>
                </Collapsible.Trigger>
              </HStack>
              <FormLayout.Description>
                Ihre bisherigen Änderungen mit altem und neuem Wert, Datum und Bearbeiter.
              </FormLayout.Description>
              <Collapsible.Content>
                {historyAvailable ? (
                  <ValueChangeHistoryTable kind="capacity" rows={history} currentUser={user} />
                ) : (
                  <Text color="fg.muted">Der Verlauf ist derzeit nicht verfügbar.</Text>
                )}
              </Collapsible.Content>
            </FormLayout.Section>
          </Collapsible.Root>
        )}

        {dirty && !loading && (
          <SettingsActionBar
            visible={dirty && !loading}
            onCancel={() => setCancelOpen(true)}
            cancelDisabled={saving}
            onSave={handleSaveClick}
            saveDisabled={!allValid || saving}
            saving={saving}
          />
        )}
      </FormLayout.Root>

      {/* explicit cancel from the action bar */}
      <DismissChangesDialog
        isOpen={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onDismiss={() => {
          resetToOriginal()
          setCancelOpen(false)
        }}
        onSave={() => {
          setCancelOpen(false)
          handleSaveClick()
        }}
        onNavigate={() => {
          // no tab navigation from this page – kept for API compatibility
        }}
      />

      <ValueChangeConfirmDialog
        isOpen={confirmOpen}
        kind="capacity"
        changes={pendingChanges}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={doSave}
        saving={saving}
      />

      <DropPlannedChangeDialog
        isOpen={dropTarget !== null}
        kind="capacity"
        serviceLabel={dropTarget ? SERVICE_LABEL[dropTarget.code] : ''}
        planned={dropTarget?.planned ?? null}
        activeValue={dropTarget ? records[dropTarget.code].activeValue : null}
        onCancel={() => setDropTarget(null)}
        onConfirm={doDrop}
        dropping={dropping}
      />
    </Box>
  )
}

function CapacityField({
  code,
  label,
  required,
  helper,
  record,
  draft,
  changed,
  valueError,
  dateRequired,
  showDateError,
  today,
  disabled,
  readOnly,
  planningYear,
  fixedValidFrom,
  onInput,
  onDate,
  onEditPlanned,
  onStopEditing,
  onDropPlanned,
}: {
  code: ServiceCode
  label: string
  required: boolean
  helper: string
  record: ServiceRecord
  draft: ServiceDraft
  changed: boolean
  valueError: boolean
  dateRequired: boolean
  showDateError: boolean
  today: IsoDate
  disabled: boolean
  /** A year that no longer takes changes (SIB-928). */
  readOnly: boolean
  /** Set for a year that hasn't started; its values may be carried over (SIB-928). */
  planningYear: number | null
  fixedValidFrom: IsoDate | null
  onInput: (v: string) => void
  onDate: (v: IsoDate | '') => void
  onEditPlanned: (p: PlannedValue) => void
  onStopEditing: () => void
  onDropPlanned: (p: PlannedValue) => void
}) {
  const inputLabel = draft.editing
    ? `Geplanter Wert ab ${formatIsoDateDE(draft.editing.validFrom)}`
    : planningYear !== null
      ? `Wert für ${planningYear}`
      : record.activeValue === null
        ? 'Wert'
        : 'Neuer Wert'
  const carriedOver = planningYear !== null && record.source === 'CARRIED_OVER'

  return (
    <Stack flex="1 1 0" minW={0} w="full" gap={3} data-testid={`capacity-service-${code}`}>
      <Field.Root invalid={valueError} required={required}>
        <Field.Label>
          {label} {required ? <Field.RequiredIndicator /> : null}
        </Field.Label>
        <Field.HelperText>{helper}</Field.HelperText>
      </Field.Root>

      {planningYear !== null && (
        <SourceBadge code={code} source={record.source} year={planningYear} />
      )}

      <ServiceValueSummary
        kind="capacity"
        serviceLabel={label}
        activeValue={record.activeValue}
        activeSince={record.activeSince}
        planned={record.planned}
        editingId={draft.editing?.id ?? null}
        onEdit={readOnly ? undefined : onEditPlanned}
        onDrop={readOnly ? undefined : onDropPlanned}
        disabled={disabled}
        {...(planningYear !== null && {
          activeLabel: `Für ${planningYear}`,
          activeNote: carriedOver
            ? `(Wert aus ${planningYear - 1})`
            : record.activeSince
              ? `(ab ${formatIsoDateDE(record.activeSince)})`
              : null,
        })}
      />

      <Field.Root invalid={valueError} required={required}>
        <Field.Label>{inputLabel}</Field.Label>
        <SettingsInput
          value={draft.input}
          onChange={e => onInput(e.target.value)}
          type="number"
          min={0}
          step={1}
          inputMode="numeric"
          disabled={disabled || readOnly}
          aria-label={`${label} ${inputLabel}`}
          placeholder=""
          data-carried-over={carriedOver && !changed ? '' : undefined}
          {...(carriedOver && !changed && { borderStyle: 'dashed', color: 'fg.muted' })}
        />
        {valueError && (
          <Field.ErrorText>
            Wert darf nicht leer sein und muss eine ganze Zahl ≥ 0 sein.
          </Field.ErrorText>
        )}
      </Field.Root>

      {(dateRequired || draft.editing) && (
        <EffectiveDatePicker
          name={`validFrom-${code}`}
          value={draft.validFrom}
          onChange={onDate}
          required={dateRequired}
          showError={showDateError}
          disabled={disabled}
          today={today}
          helperText="Änderungen gelten immer ab dem 1. eines Monats."
        />
      )}

      {draft.editing && (
        <Button variant="ghost" onClick={onStopEditing} disabled={disabled}>
          Bearbeitung abbrechen
        </Button>
      )}

      {!readOnly &&
        planningYear === null &&
        !changed &&
        record.activeValue !== null &&
        record.planned.length === 0 && (
          <Text color="fg.muted">Ändern Sie den Wert, um eine Änderung mit Datum zu planen.</Text>
        )}

      {!readOnly && carriedOver && !changed && (
        <Text color="fg.muted">Bleibt der Wert gleich, müssen Sie nichts tun.</Text>
      )}

      {!readOnly && planningYear !== null && changed && !draft.editing && fixedValidFrom && (
        <Text color="fg.muted">Gilt ab {formatIsoDateDE(fixedValidFrom)}.</Text>
      )}
    </Stack>
  )
}

/**
 * Whether a year that hasn't started has been planned for this service (SIB-928). A carried-over
 * value must never look like a decision: Union reads this to see which SIBs planned the new year.
 */
function SourceBadge({
  code,
  source,
  year,
}: {
  code: ServiceCode
  source: CapacitySource | null
  year: number
}) {
  const [palette, text] =
    source === 'SAVED'
      ? ['green', `Für ${year} erfasst`]
      : source === 'CARRIED_OVER'
        ? ['orange', `Übernommen aus ${year - 1} — noch nicht erfasst`]
        : ['gray', `Für ${year} noch kein Wert`]
  return (
    <Badge
      alignSelf="flex-start"
      colorPalette={palette}
      variant="subtle"
      whiteSpace="normal"
      data-testid={`capacity-source-${code}`}
      data-source={source ?? 'NONE'}
    >
      {text}
    </Badge>
  )
}

/** What the SIB needs to know about the year on screen before touching any value (SIB-928). */
function YearNotice({
  year,
  openYears,
  records,
}: {
  year: CapacityYear
  openYears: CapacityYear[]
  records: Records
}) {
  if (!year.editable) {
    const later = openYears.find(y => y.year > year.year && y.editable)
    return (
      <Alert.Root status="info" data-testid="capacity-year-notice">
        <Alert.Indicator />
        <Alert.Description>
          Für {year.year} können keine Änderungen mehr erfasst werden.
          {later && ` Änderungen ab Januar erfassen Sie unter ${later.year}.`}
        </Alert.Description>
      </Alert.Root>
    )
  }
  if (year.current) return null

  // An optional service the SIB never offered (Pilot without a value) is nothing left to plan.
  const notPlanned = SERVICES.filter(s => {
    const source = records[s.code].source
    return source === 'CARRIED_OVER' || (source === 'NONE' && s.required)
  }).length
  return (
    <Alert.Root status={notPlanned > 0 ? 'warning' : 'success'} data-testid="capacity-year-notice">
      <Alert.Indicator />
      <Alert.Description>
        {notPlanned > 0
          ? `Für ${year.year} ${notPlanned === 1 ? 'ist 1 Leistung' : `sind ${notPlanned} Leistungen`} noch nicht erfasst. Vorausgefüllt sind Ihre Werte aus ${year.year - 1} – ändern Sie nur, was sich ändert. Ein unveränderter Wert gilt auch ${year.year} weiter.`
          : `Alle Leistungen sind für ${year.year} erfasst.`}
        {year.nextChangeValidFrom &&
          ` Änderungen gelten ab ${formatIsoDateDE(year.nextChangeValidFrom)}.`}
      </Alert.Description>
    </Alert.Root>
  )
}
